import { Container, Graphics, type FederatedPointerEvent } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t } from '@/i18n';
import type { InputState } from '@/input/types';
import { sfx } from '@/audio/sfx';
import { crossGlyph } from './hangar-icons';
import { checkGlyph } from './home-art';
import { bodyText, chamferPanel, label, targetBrackets } from './widgets';

/** Optional icon-first dressing of a confirm (polish 1: PAUSE → MAIN MENU). */
export interface ConfirmOpts {
  /** Picture above the message — centred on x = 0, top at y = 0; rebuilt with the dialog */
  art?: () => Container;
  /** ✓ / ✕ glyph buttons instead of the CONFIRM / CANCEL words */
  glyphs?: boolean;
}

const ART_TOP = 22;
const ART_GAP = 14;
const GLYPH_S = 12;

/**
 * Reusable modal confirm — tech-styled, focus-navigable, clickable.
 * Destructive uses default to CANCEL for safety.
 */
export class ConfirmDialog {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  open = false;
  private selected = 1; // 0 = confirm, 1 = cancel (safe default)
  private message = '';
  private onConfirm: (() => void) | null = null;
  private opts: ConfirmOpts = {};
  private prevMoveX = 0;
  private screenW = 0;
  private screenH = 0;

  constructor() {
    this.view.visible = false;
    this.view.addChild(this.dim, this.layer);
  }

  show(
    screenW: number, screenH: number, message: string, onConfirm: () => void,
    defaultConfirm = false, opts: ConfirmOpts = {},
  ): void {
    this.open = true;
    this.view.visible = true;
    this.message = message;
    this.onConfirm = onConfirm;
    this.opts = opts;
    // Destructive flows default to CANCEL; flows the player just asked
    // for (overwrite-new-game) default to CONFIRM so Ⓐ goes through
    this.selected = defaultConfirm ? 0 : 1;
    this.screenW = screenW;
    this.screenH = screenH;
    this.rebuild();
  }

  private rebuild(): void {
    const { screenW, screenH } = this;
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x0a0614, alpha: 0.7 });
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));

    const text = bodyText(this.message, 16, 0xe8eaf0, 380);
    const art = this.opts.art?.() ?? null;
    const artH = art ? art.height + ART_GAP : 0;
    const w = 460;
    const h = 120 + artH + text.height;
    const panel = new Graphics();
    chamferPanel(panel, w, h, {
      fill: 0x161222, stroke: art ? COLORS.uiAccent : COLORS.dangerOrange, notch: 16,
    });
    panel.position.set(-w / 2, -h / 2);
    this.layer.addChild(panel);
    if (art) {
      art.position.set(0, -h / 2 + ART_TOP);
      this.layer.addChild(art);
    }
    text.anchor.set(0.5, 0);
    text.position.set(0, -h / 2 + 26 + artH);
    this.layer.addChild(text);

    const options: ReadonlyArray<{ key: 'confirm.yes' | 'confirm.no'; danger: boolean }> = [
      { key: 'confirm.yes', danger: true },
      { key: 'confirm.no', danger: false },
    ];
    options.forEach((opt, i) => {
      const selected = this.selected === i;
      const bw = 180;
      const btn = new Container();
      const bg = new Graphics();
      chamferPanel(bg, bw, 50, {
        fill: selected ? (opt.danger ? 0x3a1420 : 0x2a2440) : 0x1a1626,
        stroke: selected ? (opt.danger ? COLORS.dangerRed : COLORS.uiAccent) : 0x4a4256,
        strokeWidth: selected ? 4 : 3,
        notch: 12,
      });
      if (selected) {
        targetBrackets(bg, bw, 50, opt.danger ? COLORS.dangerRed : COLORS.uiAccent, 12, 4);
      }
      bg.position.set(-bw / 2, -25);
      btn.addChild(bg);
      if (this.opts.glyphs) {
        const g = new Graphics();
        if (opt.danger) checkGlyph(g, 0, 0, GLYPH_S, COLORS.statUp);
        else crossGlyph(g, 0, 0, GLYPH_S * 0.75, selected ? 0xffffff : 0xb4bac4);
        btn.addChild(g);
      } else {
        const txt = label(t(opt.key), 16, selected && opt.danger ? COLORS.dangerRed : 0xffffff, 2);
        txt.anchor.set(0.5);
        btn.addChild(txt);
      }
      btn.position.set((i === 0 ? -1 : 1) * 110, h / 2 - 50);
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.on('pointerover', (e: FederatedPointerEvent) => {
        // Mouse only: on touch the rebuild would eat the tap (wave 4 rule)
        if (e.pointerType === 'mouse' && this.selected !== i) {
          this.selected = i;
          this.rebuild();
        }
      });
      btn.on('pointertap', () => this.activate(i));
      this.layer.addChild(btn);
    });

    this.layer.position.set(screenW / 2, screenH / 2);
  }

  private activate(i: number): void {
    const confirm = i === 0;
    // hide() clears the callback — keep it for the ✓ action
    const onConfirm = this.onConfirm;
    this.hide();
    if (confirm) {
      sfx.uiConfirm();
      onConfirm?.();
    } else {
      sfx.uiMove();
    }
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
    this.onConfirm = null;
  }

  /** Consumes input while open. @returns true if the caller must skip its update */
  handleInput(input: InputState): boolean {
    if (!this.open) return false;
    const edge =
      Math.abs(input.moveX) > 0.5 && Math.abs(this.prevMoveX) <= 0.5
        ? Math.sign(input.moveX)
        : 0;
    this.prevMoveX = input.moveX;
    if (edge !== 0) {
      this.selected = this.selected === 0 ? 1 : 0;
      sfx.uiMove();
      this.rebuild();
    }
    if (input.dashPressed) this.activate(this.selected);
    else if (input.pausePressed || input.altPressed) this.activate(1);
    return true;
  }
}
